import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import SplashScreen from './components/SplashScreen';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Candidates from './pages/Candidates';
import Requirements from './pages/Requirements';
import Users from './pages/Users';
import ActivityLogs from './pages/ActivityLogs';
import TeamDashboard from './pages/TeamDashboard';
import Teams         from './pages/Teams';
import Tasks         from './pages/Tasks';
import EmailDashboard from './pages/EmailDashboard';
import Reports from './pages/Reports';
import Interviews from './pages/Interviews';
import Settings from './pages/Settings';
import Schedulers from './pages/Schedulers';
import ServerLogs from './pages/ServerLogs';
import CandidateFinder from './pages/CandidateFinder';
import CandidateProfile from './pages/CandidateProfile';
import Submissions from './pages/Submissions';
import CTCCalculator from './pages/CTCCalculator';
import SSOCallback from './pages/SSOCallback';
import Impersonate from './pages/Impersonate';

const LayoutWrapper = ({ children }) => <Layout>{children}</Layout>;

// Inner component so it can read the theme for Toaster styling
function AppInner({ showSplash, handleSplashDone }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <>
      {showSplash && <SplashScreen onDone={handleSplashDone} />}
      <div style={{ opacity: showSplash ? 0 : 1, transition: 'opacity 0.4s ease' }}>
        <AuthProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              containerStyle={{ zIndex: 999999 }}
              toastOptions={{
                duration: 3500,
                style: isDark ? {
                  background: 'rgba(15,23,42,0.95)',
                  backdropFilter: 'blur(12px)',
                  color: '#f1f5f9',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                } : {
                  background: '#ffffff',
                  color: '#0f172a',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                },
                success: {
                  iconTheme: { primary: '#34d399', secondary: isDark ? '#fff' : '#059669' },
                  style: { borderLeft: '3px solid #34d399' },
                },
                error: {
                  iconTheme: { primary: '#f87171', secondary: isDark ? '#fff' : '#dc2626' },
                  style: { borderLeft: '3px solid #f87171' },
                },
              }}
            />
            <ErrorBoundary>
              <Routes>
                <Route path="/login"        element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/sso-callback" element={<SSOCallback />} />
                <Route path="/dashboard" element={<ProtectedRoute><LayoutWrapper><Dashboard /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/clients" element={<ProtectedRoute roles={['SuperAdmin','RecruiterManager','Developer']}><LayoutWrapper><Clients /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/candidates" element={<ProtectedRoute><LayoutWrapper><Candidates /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/requirements" element={<ProtectedRoute><LayoutWrapper><Requirements /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute roles={['SuperAdmin']}><LayoutWrapper><Users /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/activity-logs" element={<ProtectedRoute roles={['SuperAdmin']}><LayoutWrapper><ActivityLogs /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/team-dashboard" element={<ProtectedRoute><LayoutWrapper><TeamDashboard /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/teams" element={<ProtectedRoute><LayoutWrapper><Teams /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/tasks" element={<ProtectedRoute><LayoutWrapper><Tasks /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/emails" element={<ProtectedRoute roles={['SuperAdmin','RecruiterManager','Recruiter','Developer']}><LayoutWrapper><EmailDashboard /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute roles={['SuperAdmin','RecruiterManager','Developer']}><LayoutWrapper><Reports /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/interviews" element={<ProtectedRoute><LayoutWrapper><Interviews /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute roles={['SuperAdmin']}><LayoutWrapper><Settings /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/schedulers" element={<ProtectedRoute roles={['SuperAdmin']}><LayoutWrapper><Schedulers /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/server-logs" element={<ProtectedRoute roles={['SuperAdmin']}><LayoutWrapper><ServerLogs /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/submissions" element={<ProtectedRoute><LayoutWrapper><Submissions /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/ctc-calculator" element={<ProtectedRoute roles={['SuperAdmin','RecruiterManager','Recruiter','Developer']}><LayoutWrapper><CTCCalculator /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/candidate-finder" element={<ProtectedRoute roles={['SuperAdmin','RecruiterManager','Recruiter','Developer']}><LayoutWrapper><CandidateFinder /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/candidate-profile/:id" element={<ProtectedRoute roles={['SuperAdmin','RecruiterManager','Recruiter','Developer']}><LayoutWrapper><CandidateProfile /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/impersonate" element={<ProtectedRoute roles={['RecruiterManager','Recruiter','Developer']}><LayoutWrapper><Impersonate /></LayoutWrapper></ProtectedRoute>} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </AuthProvider>
      </div>
    </>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(
    () => !sessionStorage.getItem('aurx_splashed')
  );
  const handleSplashDone = useCallback(() => {
    sessionStorage.setItem('aurx_splashed', '1');
    setShowSplash(false);
  }, []);

  return (
    <ThemeProvider>
      <AppInner showSplash={showSplash} handleSplashDone={handleSplashDone} />
    </ThemeProvider>
  );
}